import { AfterViewInit, Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { DxLookupComponent, DxLookupModule } from 'devextreme-angular';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { CurrentUserService } from '../../user/services';
import { Distribution, DistributionSearch } from '../models';
import { DistributionService } from '../services';
import { DxoDropDownOptionsModule } from 'devextreme-angular/ui/nested';
import { DxTemplateModule } from 'devextreme-angular/core';


@Component({
    selector: 'ep-distribution-selector',
    templateUrl: './distribution-selector.component.html',
    styleUrls: ['./distribution-selector.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [DxLookupModule, DxoDropDownOptionsModule, DxTemplateModule]
})
export class DistributionSelectorComponent implements OnInit, OnChanges, AfterViewInit {
    distributionDataSource: any;
    hasPermissionToSearch = false;
    loadingDetails = false;
    showLoadingDetails = true;
    distributionsToSearchIn: DistributionSearch[] = [];

    @Input() isRequired = false;
    @Input() isReadOnly = false;
    @Input() selectedDistribution: Distribution;
    @Input() showLoadingPartnerDetails = true;
    @Input() showFallbackDistributions = false;
    @Output() selectedDistributionChange: EventEmitter<Distribution> = new EventEmitter<Distribution>();
    @Output() selectedDistributionIdChange: EventEmitter<number> = new EventEmitter<number>();
    @ViewChild('distributionLookup', { static: true }) private distributionLookup: DxLookupComponent;

    private currentUserService = inject(CurrentUserService);
    private distributionService = inject(DistributionService);

    private compareString = (a: string, b: string): number => (a ?? '').localeCompare(b ?? '');

    ngOnInit(): void {
        this.hasPermissionToSearch = this.currentUserService.hasPermission('Distribution_View_Search');
        this.ngOnInitAsync();
    }

    async ngOnInitAsync() {
        this.distributionsToSearchIn = (await this.distributionService.searchDistributions({ SplitFromAndTo: false })).DistributionGroups
            .flatMap(e => e.DistributionObjectContainers)
            .flatMap(e => e.Distributions)
            .filter(e => this.showFallbackDistributions || !this.isFallbackDistribution(e))
            .sort((a, b) =>
                this.compareString(a.FromPartner, b.FromPartner) ||
                this.compareString(a.ToPartner, b.ToPartner) ||
                this.compareString(a.DistributionObjectName, b.DistributionObjectName) ||
                this.compareString(a.FromFormat, b.FromFormat) ||
                this.compareString(a.FromVariant, b.FromVariant) ||
                this.compareString(a.FromVersion, b.FromVersion) ||
                this.compareString(a.ToFormat, b.ToFormat) ||
                this.compareString(a.ToVariant, b.ToVariant) ||
                this.compareString(a.ToVersion, b.ToVersion) ||
                this.compareString(a.TransportConfigurationTo, b.TransportConfigurationTo) ||
                a.DistributionId - b.DistributionId
            );

        const store = new CustomStore({
            load: (loadOptions: any) => {
                let result = this.distributionsToSearchIn;

                const searchValue = (loadOptions.searchValue as string)?.toLowerCase().trim();
                if (searchValue?.length > 0) {
                    result = this.distributionsToSearchIn.filter(d => (
                        d.DistributionId?.toString().indexOf(searchValue) >= 0 ||
                        d.DistributionObjectName?.toLowerCase().indexOf(searchValue) >= 0 ||

                        d.FromPartner?.toLowerCase().indexOf(searchValue) >= 0 ||
                        d.FromFormat?.toLowerCase().indexOf(searchValue) >= 0 ||
                        d.FromVariant?.toLowerCase().indexOf(searchValue) >= 0 ||
                        d.FromVersion?.toLowerCase().indexOf(searchValue) >= 0 ||

                        d.ToPartner?.toLowerCase().indexOf(searchValue) >= 0 ||
                        d.ToFormat?.toLowerCase().indexOf(searchValue) >= 0 ||
                        d.ToVariant?.toLowerCase().indexOf(searchValue) >= 0 ||
                        d.ToVersion?.toLowerCase().indexOf(searchValue) >= 0 ||

                        d.TransportConfigurationTo?.toLowerCase().indexOf(searchValue) >= 0
                    ));
                }

                const limit = 100; // Limit the results to the first 100
                if (result.length > limit) {
                    result = result.slice(0, limit);
                    result.push({ moreResultTemplate: true, disabled: true } as any);
                }

                return result;
            },
            byKey: (key: any) => key
        });

        this.distributionDataSource = new DataSource({ store, paginate: false });
    }

    ngAfterViewInit(): void {
        if (this.selectedDistribution) {
            // setSelectedPartner can have been called before the AfterViewInit event - and the DevExpress widget is not ready before this event.
            // Therefore set the selected partnere here if it has been set before.
            this.setSelectedDistribution(this.selectedDistribution);
        }
    }

    ngOnChanges(changes: any): void {
        if (changes.selectedDistribution) {
            this.setSelectedDistribution(this.selectedDistribution);
        }
    }

    reset(): void {
        this.setSelectedDistribution(null);
    }

    async setSelectedDistributionId(distributionId: number) {
        this.loadingDetails = true;

        const result = await this.distributionService.getDistributions([distributionId]);
        if (result.length > 0) {
            this.setSelectedDistribution(result[0]);
        }

        this.loadingDetails = false;
    }

    setSelectedDistribution(distribution: Distribution): void {
        if (this.distributionLookup.instance) {
            if (distribution) {
                this.distributionLookup.isValid = true;
                // Create an instance of the PartnerSearch object so the devexpress template can be re-used (Partner-object != PartnerSearch-object).
                const distributionSearch = {} as DistributionSearch;
                distributionSearch.DistributionId = distribution.DistributionsId;
                distributionSearch.DistributionObjectName = distribution.DocumentConfigIn.DistributionObject.Name;
                distributionSearch.FromFormat = distribution.DocumentConfigIn.Format.FormatName;
                distributionSearch.FromVariant = distribution.DocumentConfigIn.Variant.VariantName;
                distributionSearch.FromVersion = distribution.DocumentConfigIn.VariantVersion.VariantVersionName;
                distributionSearch.FromPartner = distribution.PartnerIn.PartnerName;

                distributionSearch.ToFormat = distribution.DocumentConfigOut.Format.FormatName;
                distributionSearch.ToVariant = distribution.DocumentConfigOut.Variant.VariantName;
                distributionSearch.ToVersion = distribution.DocumentConfigOut.VariantVersion.VariantVersionName;
                distributionSearch.ToPartner = distribution.PartnerOut.PartnerName;

                distributionSearch.TransportConfigurationTo = distribution.TransportConfigurationOut.Name;

                // IMPORTANT! This value is set to prevent an infinite loop - because calling the below option method will trigger the 'partnerSelectedInternal' method.
                (distributionSearch as any).ignore = true;

                this.distributionLookup.instance.option('value', distributionSearch);
            }
            else {
                if (this.isRequired) {
                    this.distributionLookup.isValid = false;
                }
                this.distributionLookup.instance.option('value', null);
            }
        }
    }

    distributionSelectedInternal(e: any): void {
        if (e && e.value && !e.value.ignore) {
            const distributionSearch = e.value as DistributionSearch;

            // Get the real Partner-object from the backend
            if (this.selectedDistributionChange.observers.length > 0) {
                this.distributionLookup.isValid = true;
                this.loadingDetails = true;
                this.distributionService.getDistributions([distributionSearch.DistributionId]).then(dists => {
                    const dist = dists[0];
                    this.selectedDistributionChange.emit(dist);
                    this.selectedDistribution = dist;
                    this.loadingDetails = false;
                });
            }

            this.selectedDistributionIdChange.emit(distributionSearch.DistributionId);
        }
        else if (e && !e.value) {
            if (this.isRequired) {
                this.distributionLookup.isValid = false;
            }
            this.selectedDistributionChange.emit(null);
            this.selectedDistributionIdChange.emit(null);
            this.selectedDistribution = null;
        }
    }

    isValid(): boolean {
        return this.distributionLookup.isValid;
    }

    isFallbackDistribution(distribution: DistributionSearch): boolean {
        return this.distributionService.isFallbackDistributionSearch(distribution);
    }
}
